import { useCallback, useEffect, useRef } from "react";

const learningTtsHttpUrl = (): string =>
  typeof window === "undefined" ? "/api/audio/learning-tts" : `${window.location.origin}/api/audio/learning-tts`;

export function useLearningMessageTts({
  getToken,
  playbackRate = 1,
}: {
  getToken: () => Promise<string | null>;
  playbackRate?: number;
}) {
  const requestAbortRef = useRef<AbortController | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const handlersRef = useRef<{
    onPlaybackEnd?: () => void;
    onError?: () => void;
    onPlaybackStart?: () => void;
    onMetadata?: (durationSeconds: number) => void;
    onTimeUpdate?: (currentTimeSeconds: number, durationSeconds: number) => void;
  }>({});
  const suppressNextAudioErrorRef = useRef(false);
  const playbackRateRef = useRef(playbackRate);
  playbackRateRef.current = playbackRate;

  const teardownAudioElement = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    suppressNextAudioErrorRef.current = true;
    audio.onended = null;
    audio.onerror = null;
    audio.onloadedmetadata = null;
    audio.onplaying = null;
    audio.ontimeupdate = null;
    audio.pause();
    audio.src = "";
    audioRef.current = null;
  }, []);

  const stopSession = useCallback(() => {
    if (requestAbortRef.current) {
      requestAbortRef.current.abort();
      requestAbortRef.current = null;
    }
    teardownAudioElement();
  }, [teardownAudioElement]);

  const pauseSession = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  const resumeSession = useCallback(() => {
    if (!audioRef.current) {
      return;
    }
    void audioRef.current.play().catch(() => {
      handlersRef.current.onError?.();
    });
  }, []);

  const setHandlers = useCallback(
    (next: {
      onPlaybackEnd?: () => void;
      onError?: () => void;
      onPlaybackStart?: () => void;
      onMetadata?: (durationSeconds: number) => void;
      onTimeUpdate?: (currentTimeSeconds: number, durationSeconds: number) => void;
    }) => {
      handlersRef.current = next;
    },
    []
  );

  const startSession = useCallback(
    async (plainText: string, messageId?: string) => {
      stopSession();
      const trimmed = plainText.trim();
      if (!trimmed) {
        return;
      }

      const token = await getToken();
      if (!token) {
        handlersRef.current.onError?.();
        return;
      }
      const abortController = new AbortController();
      requestAbortRef.current = abortController;
      const url = learningTtsHttpUrl();
      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ text: trimmed, token, messageId }),
          signal: abortController.signal,
          redirect: "error",
        });

        if (!response.ok) {
          handlersRef.current.onError?.();
          return;
        }

        const data = (await response.json()) as { audioUrl?: string };
        if (!data.audioUrl || abortController.signal.aborted || requestAbortRef.current !== abortController) {
          handlersRef.current.onError?.();
          return;
        }

        if (typeof window === "undefined") {
          handlersRef.current.onError?.();
          return;
        }

        const audio = new Audio(
          data.audioUrl.startsWith("http") ? data.audioUrl : `${window.location.origin}${data.audioUrl}`
        );
        suppressNextAudioErrorRef.current = false;
        audio.playbackRate = playbackRateRef.current;
        audioRef.current = audio;
        audio.onended = () => {
          handlersRef.current.onPlaybackEnd?.();
        };
        audio.onerror = () => {
          if (suppressNextAudioErrorRef.current) {
            suppressNextAudioErrorRef.current = false;
            return;
          }
          handlersRef.current.onError?.();
        };
        audio.onloadedmetadata = () => {
          if (Number.isFinite(audio.duration)) {
            handlersRef.current.onMetadata?.(audio.duration);
          }
          if (Number.isFinite(audio.currentTime) && Number.isFinite(audio.duration)) {
            handlersRef.current.onTimeUpdate?.(audio.currentTime, audio.duration);
          }
        };
        audio.onplaying = () => {
          handlersRef.current.onPlaybackStart?.();
        };
        audio.ontimeupdate = () => {
          if (!Number.isFinite(audio.currentTime)) {
            return;
          }
          const durationSeconds = Number.isFinite(audio.duration) ? audio.duration : 0;
          handlersRef.current.onTimeUpdate?.(audio.currentTime, durationSeconds);
        };

        await audio.play();
      } catch (err) {
        if (!abortController.signal.aborted) {
          handlersRef.current.onError?.();
        }
      } finally {
        if (requestAbortRef.current === abortController) {
          requestAbortRef.current = null;
        }
      }
    },
    [getToken, stopSession]
  );

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return undefined;
    }
    const handlePageHidden = () => {
      stopSession();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        stopSession();
      }
    };
    window.addEventListener("pagehide", handlePageHidden);
    window.addEventListener("beforeunload", handlePageHidden);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("pagehide", handlePageHidden);
      window.removeEventListener("beforeunload", handlePageHidden);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [stopSession]);

  return { startSession, stopSession, pauseSession, resumeSession, setHandlers };
}
